import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, Grid, useTheme } from "@mui/material";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ServicesURL, getAllErrorsAsString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import AnimationFadeIn from "@components/AnimationFadeIn";
import AccountingProjectFormCst from "../AccountingProjectFormCst";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import ModalCst, { OutcomesInterface } from "@components/ModalCst";
import { useAddEditCostProject } from "../../hooks/useAddEditCostProject";

interface AccountingCostProjectInterface {
  onCancelAction: () => void;
  onSubmitAction: (data: any, list?: any) => void;
  idCostEdit?: string;
}

const AccountingCostProject: React.FC<AccountingCostProjectInterface> = ({
  onCancelAction,
  onSubmitAction,
  idCostEdit,
}) => {
  const { t } = useTranslation(["projects-cost-page"]);
  const { id } = useParams();
  const [readOnly, setReadOnly] = useState(false);

  const { columns, dataDetail, mutate } = useFetchDetail(
    `${ServicesURL.getProjectsCost}/${idCostEdit}/accounting-records`,
    `?corporateId=${id}`,
    PagesID["projects.cost-accounting"],
  );

  const { dataDetail: dataCost } = useFetchDetail(
    `${ServicesURL.getProjectsCost}`,
    idCostEdit,
  );

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerAccountingProjectCost } = useSWRMutation(
    `${ServicesURL.postProjectsCost}/${idCostEdit}/accounting-records/bulk`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.POST, { ...arg.arg }),
  );

  const getInitialOutcome = () => {
    return {
      budget: {
        code: undefined,
        message: t("accounting.outcome.project.messageLoading"),
        title: t("accounting.outcome.project.title"),
      },
    };
  };

  const initialOutcome: OutcomesInterface = {
    budget: {
      code: undefined,
      message: t("accounting.outcome.project.messageLoading"),
      title: t("accounting.outcome.project.title"),
    },
  };

  const [outcome, setOutcome] = useState<OutcomesInterface>(initialOutcome);
  const [openOutcome, setOpenOutcome] = useState(false);

  const onSubmit = async (inputForm: any, details: any) => {
    setOpenOutcome(true);
    const initialOutcomeState: any = getInitialOutcome();
    setOutcome(initialOutcomeState);
    const newOutcome: any = JSON.parse(JSON.stringify(initialOutcomeState));
    const keys = Object.keys(inputForm);
    const pairs: { budget_id: Number; value: Number }[] = [];

    Object.keys(inputForm)
      .filter((key) => key.startsWith("budget_"))
      .forEach((budgetKey) => {
        const index = budgetKey.split("_")[1];
        const valueKey = `value_${index}`;
        if (
          inputForm[budgetKey] !== undefined &&
          inputForm[valueKey] !== undefined
        ) {
          pairs.push({
            budget_id: Number(inputForm[budgetKey]),
            value: Number(inputForm[valueKey]),
          });
        }
      });
    const objToSend = { accounting: pairs };

    try {
      await triggerAccountingProjectCost(objToSend);
      newOutcome.budget.code = "OK";
      newOutcome.budget.message = t("accounting.outcome.project.messageOK");
      setOutcome(newOutcome);
    } catch (error) {
      newOutcome.budget.code = "KO";
      newOutcome.budget.message = getAllErrorsAsString(error);
      setOutcome(newOutcome);
    }

    mutate && mutate(inputForm);
  };

  const getColumns = useMemo(() => {
    return columns.map((el) => {
      return { ...el };
    });
  }, [dataDetail, columns]);

  return (
    <AnimationFadeIn>
      {columns && (
        <Box
          sx={{
            mt: "25px",
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            sx={{
              textAlign: "center",
            }}
          >
            {t("hero-title-accounting")}
          </Typography>

          <AccountingProjectFormCst
            row={dataDetail || []}
            columns={getColumns}
            onSubmit={onSubmit}
            backButton={() => {
              onCancelAction();
            }}
            dataCost={dataCost || []}
            readOnly={readOnly}
            fullWidth={true}
          />
        </Box>
      )}

      <ModalCst
        open={openOutcome}
        setOpen={setOpenOutcome}
        typeOfModal={"outcome"}
        data={{
          outcome: outcome,
          callBackSubmit: () => {
            onCancelAction();
            onSubmitAction(undefined, undefined);
          },
        }}
      />
    </AnimationFadeIn>
  );
};

export default AccountingCostProject;
